import { useState, useEffect, useCallback } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors, formateTimeRange } from "../utils";

const Plot = createPlotlyComponent(Plotly);

const LinePlot = ({
  isLoading,
  data,
  timebuckets,
  activeDomains,
  activeProxy,
}) => {
  const [chartData, setChartData] = useState();
  const [chartLayout, setChartLayout] = useState();
  const [showPlot, setShowPlot] = useState(false);

  const createChartLayout = () => {
    const plotLayout = {
      title: "",
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      xaxis: {
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
        type: "date",
        tickformat: "%Y",
      },
      yaxis: {
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
      },
      font: {
        color: "white",
      },
    };
    setChartLayout(plotLayout);
  };

  const createChartData = useCallback(() => {
    if (!data[activeProxy.label]) return null;

    // Only plot timestamp buckets from 2015 and up
    const slicedTimeBuckets = timebuckets?.slice(5, timebuckets?.length);
    const formattedTimeBuckets = slicedTimeBuckets?.map((timebucket) =>
      formateTimeRange(timebucket)
    );

    const plotData = activeDomains.map((domain, idx) => {
      // Create interval notation for each domain datapoint and every timebucket
      const intervals = slicedTimeBuckets.map((timebucket) => {
        return `${
          data[activeProxy.label][domain.value]?.value?.slice(
            5,
            timebuckets?.length
          )[slicedTimeBuckets.indexOf(timebucket)]
        } [${
          data[activeProxy.label][domain.value]?.value_lb?.slice(
            5,
            timebuckets?.length
          )[slicedTimeBuckets.indexOf(timebucket)]
        }, ${
          data[activeProxy.label][domain.value]?.value_ub?.slice(
            5,
            timebuckets?.length
          )[slicedTimeBuckets.indexOf(timebucket)]
        }]`;
      });

      // Generate chart data objects for value, upper bound and lower bound
      return [
        {
          x: formattedTimeBuckets.map((timestamp) =>
            timestamp.split("/")[0].trim()
          ),
          y: intervals.map((interval) =>
            parseInt(interval.split(" [")[1].split(", ")[1].slice(0, -1))
          ),
          type: "scatter",
          mode: "lines",
          line: { color: "transparent" },
          showlegend: false,
          hoverinfo: "none",
        },
        {
          x: formattedTimeBuckets.map((timestamp) =>
            timestamp.split("/")[0].trim()
          ),
          y: intervals.map((interval) =>
            parseInt(interval.split("[")[1].split(", ")[0])
          ),
          type: "scatter",
          mode: "lines",
          fill: "tonexty",
          fillcolor: colors[idx].replace("rgb", "rgba").replace(")", ",0.4)"),
          line: { color: "transparent" },
          showlegend: false,
          hoverinfo: "none",
        },
        {
          x: formattedTimeBuckets.map((timestamp) =>
            timestamp.split("/")[0].trim()
          ),
          y: intervals.map((interval) =>
            parseInt(interval.split(" [")[0].split(",")[0])
          ),
          type: "scatter",
          mode: "lines+markers",
          line: { color: colors[idx], width: 2 },
          marker: { size: 6 },
          name: domain.label,
          customdata: intervals,
          hovertemplate: `<b>${domain.label}</b>: %{customdata}<extra></extra>`,
        },
      ];
    });
    setChartData(plotData.flat());
  }, [activeDomains, activeProxy, data, timebuckets]);

  useEffect(() => {
    if (
      isLoading ||
      !data ||
      activeProxy === null ||
      activeDomains.length === 0
    ) {
      setShowPlot(false);
      setChartLayout(null);
      setChartData(null);
      return;
    }

    setShowPlot(true);
    createChartData();
    createChartLayout();
  }, [activeDomains, activeProxy, data, createChartData, isLoading]);

  return (
    <div className="flex flex-col items-center content-center min-w-50">
      {isLoading && <p className="text-center">Loading data...</p>}
      {!isLoading && chartData && chartLayout && showPlot && (
        <Plot data={chartData} layout={chartLayout} />
      )}
    </div>
  );
};

export default LinePlot;
